import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSocket } from "context/SocketContext";
import {
  addMealIndex,
  clearPendingRecreation,
  getMealRecreationKey,
} from "@redux/slices/selectedMealsSlice";

const findPendingForSocketResult = (pendingRecreations, result) => {
  const entries = Object.values(pendingRecreations || {}).filter(
    (entry) => entry?.type === "api",
  );

  const jobId = result?.job_id;
  if (jobId) {
    const byJob = entries.find((entry) => entry.jobId === jobId);
    if (byJob) return byJob;
  }

  const dietPlan = result?.data?.diet_plan || result?.diet_plan;
  if (dietPlan) {
    const matched = entries.find((pending) => {
      const newLength =
        dietPlan?.[pending.day]?.Meals?.[pending.mealType]?.length ?? 0;
      return newLength > (pending.previousLength ?? 0);
    });
    if (matched) return matched;
  }

  return entries.length === 1 ? entries[0] : null;
};

/**
 * Keeps meal recreate jobs/countdowns alive across route changes.
 * Shows toast when socket/countdown finishes even if user left select-meals.
 */
const MealRecreationManager = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const socket = useSocket() || {};
  const { singleMealQueue = [], consumeSingleMealResult } = socket;
  const pendingRecreations = useSelector(
    (state) => state.selectedMeals.pendingRecreations || {},
  );
  const countdownTimersRef = useRef({});

  const showCreatedToast = (day, mealTypeLower) => {
    toast.info(
      t("toast.New day mealType created successfully!", {
        day: t(`days.${day.toLowerCase()}`),
        mealType: t(`weeklyMealDetails.${mealTypeLower}`),
      }),
    );
  };

  const completeCountdown = (pending) => {
    if (!pending?.day || !pending?.mealType) return;

    const key = getMealRecreationKey(pending.day, pending.mealType);
    if (countdownTimersRef.current[key]) {
      clearTimeout(countdownTimersRef.current[key]);
      delete countdownTimersRef.current[key];
    }

    dispatch(
      addMealIndex({
        day: pending.day,
        mealType: pending.mealTypeLower,
        index: pending.hiddenIndex,
      }),
    );
    dispatch(
      clearPendingRecreation({
        day: pending.day,
        mealType: pending.mealType,
      }),
    );
    showCreatedToast(pending.day, pending.mealTypeLower);
  };

  // Schedule / resume countdown timers from endsAt (survives leaving the page)
  useEffect(() => {
    Object.entries(pendingRecreations).forEach(([key, pending]) => {
      if (pending?.type !== "countdown") return;
      if (countdownTimersRef.current[key]) return;

      const remainingMs = (pending.endsAt || 0) - Date.now();
      if (remainingMs <= 0) {
        countdownTimersRef.current[key] = setTimeout(() => {
          completeCountdown(pending);
        }, 0);
        return;
      }

      countdownTimersRef.current[key] = setTimeout(() => {
        completeCountdown(pending);
      }, remainingMs);
    });

    Object.keys(countdownTimersRef.current).forEach((key) => {
      if (!pendingRecreations[key] || pendingRecreations[key]?.type !== "countdown") {
        clearTimeout(countdownTimersRef.current[key]);
        delete countdownTimersRef.current[key];
      }
    });
  }, [pendingRecreations]);

  // Process socket meal.generated globally (toast even off select-meals)
  useEffect(() => {
    if (!singleMealQueue?.length) return;

    const result = singleMealQueue[0];
    const pending = findPendingForSocketResult(pendingRecreations, result);

    if (result?.status === "failed") {
      if (pending) {
        dispatch(
          clearPendingRecreation({
            day: pending.day,
            mealType: pending.mealType,
          }),
        );
      }
      consumeSingleMealResult?.();
      return;
    }

    if (result?.status === "completed" || result?.data?.status) {
      if (!pending) {
        // No matching pending on this client (e.g. already cleared) — drop event
        consumeSingleMealResult?.();
        return;
      }

      const planPayload = result?.data?.diet_plan
        ? result.data
        : result?.diet_plan
          ? result
          : null;

      if (planPayload?.diet_plan) {
        const newMeals =
          planPayload.diet_plan?.[pending.day]?.Meals?.[pending.mealType];
        const newIndex = Array.isArray(newMeals) ? newMeals.length - 1 : -1;

        if (newIndex >= 0 && newMeals[newIndex]?.MealName) {
          dispatch(
            addMealIndex({
              day: pending.day,
              mealType: pending.mealTypeLower,
              index: newIndex,
            }),
          );
          showCreatedToast(pending.day, pending.mealTypeLower);
        } else {
          toast.error(t("toast.Failed to create new meal. Please try again."));
        }
      } else {
        toast.error(t("toast.Failed to create new meal. Please try again."));
      }

      dispatch(
        clearPendingRecreation({
          day: pending.day,
          mealType: pending.mealType,
        }),
      );
    }

    consumeSingleMealResult?.();
  }, [singleMealQueue, pendingRecreations, consumeSingleMealResult, dispatch, t]);

  useEffect(() => {
    return () => {
      Object.values(countdownTimersRef.current).forEach(clearTimeout);
      countdownTimersRef.current = {};
    };
  }, []);

  return null;
};

export default MealRecreationManager;
